// School-admin → Teachers. List active + pending teachers, invite by email,
// remove. docs/replan-v2.md R9.

import { useT } from '@fajrarabi/i18n/react';
import { Alert, Badge, Button, Card, CardBody, Field, Input, Skeleton } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { ApiError } from '~/lib/api';
import { inviteTeacher, listTeachers, removeTeacher, type Teacher } from '~/lib/school';

export const Route = createFileRoute('/$locale/school/teachers')({
  component: TeachersScreen,
});

function TeachersScreen() {
  const t = useT();
  const qc = useQueryClient();
  const teachers = useQuery({
    queryKey: ['school', 'teachers'],
    queryFn: ({ signal }) => listTeachers(signal),
  });

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () => inviteTeacher(email.trim(), name.trim()),
    onSuccess: async () => {
      setNotice(t('app.school.invite_sent'));
      setEmail('');
      setName('');
      await qc.invalidateQueries({ queryKey: ['school', 'teachers'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => removeTeacher(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['school', 'teachers'] });
    },
  });

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-bold text-ink-900">{t('app.school.teachers_title')}</h1>

      <Card className="mt-6">
        <CardBody>
          {notice && (
            <Alert tone="success" className="mb-4">
              {notice}
            </Alert>
          )}
          {invite.isError && (
            <Alert tone="danger" className="mb-4">
              {invite.error instanceof ApiError
                ? invite.error.message
                : t('app.common.error_generic')}
            </Alert>
          )}
          <form
            className="grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              setNotice(null);
              invite.mutate();
            }}
          >
            <Field label={t('app.school.teacher_name')}>
              {(id) => (
                <Input id={id} value={name} onChange={(e) => setName(e.target.value)} required />
              )}
            </Field>
            <Field label={t('app.school.teacher_email')}>
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              )}
            </Field>
            <Button type="submit" disabled={invite.isPending}>
              {t('app.school.send_invite')}
            </Button>
          </form>
        </CardBody>
      </Card>

      <div className="mt-8 space-y-3">
        {teachers.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (teachers.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-ink-700">{t('app.school.no_teachers')}</p>
        ) : (
          teachers.data!.map((teacher) => (
            <TeacherRow
              key={teacher.id}
              teacher={teacher}
              onRemove={() => remove.mutate(teacher.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function TeacherRow({ teacher, onRemove }: { teacher: Teacher; onRemove: () => void }) {
  const t = useT();
  return (
    <div className="flex items-center justify-between rounded-2xl bg-white p-4 shadow-[0_12px_30px_-22px_rgba(31,26,23,0.25)] ring-1 ring-burgundy-500/10">
      <div className="min-w-0">
        <p className="truncate font-semibold text-ink-900">
          {teacher.displayName ?? teacher.email}
        </p>
        <p className="truncate text-sm text-ink-500">{teacher.email}</p>
      </div>
      <div className="flex items-center gap-3">
        <Badge tone={teacher.status === 'active' ? 'success' : 'neutral'}>
          {teacher.status === 'active'
            ? t('app.school.status_active')
            : t('app.school.status_invited')}
        </Badge>
        <Button variant="ghost" size="sm" onClick={onRemove}>
          {t('app.school.remove')}
        </Button>
      </div>
    </div>
  );
}
